import {
  ButtonV2,
  IconCollapsed,
  IconExpanded,
  Loading,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useRef } from 'react';
import { usePreserveScrollAnchor } from '@/components/InvoiceReportDetailDrawer/hooks/usePreserveScrollAnchor';
import styles from './styles.module.scss';

const cx = classnames.bind(styles);

export type ShowRemainingButtonProps = {
  isExpanded: boolean;
  isLoading?: boolean;
  onShowRemaining: () => void;
  onCollapse: () => void;
  collapseLabel: string;
  showAllLabel: string;
  testIdPrefix: string;
};

/**
 * 「残りをすべて表示」 / 「表示をたたむ」 toggle shared by the Transaction
 * Details and Journal card lists (NA-5530, NA-5529). Loading state only
 * applies to the expand direction — collapsing is instant, the records
 * already being in memory. Journal never passes `isLoading` since its
 * entries are always fetched in full up front.
 *
 * `color="primary"` + `isTertiary` is the Figma "ButtonDefault" look
 * (white→`#ebf1fb` gradient fill, `#1b1b1b` text, gray border) — it's the
 * closest ButtonV2 combination to that spec.
 */
export const ShowRemainingButton: FC<ShowRemainingButtonProps> = memo(
  ({
    isExpanded,
    isLoading = false,
    onShowRemaining,
    onCollapse,
    collapseLabel,
    showAllLabel,
    testIdPrefix,
  }) => {
    // Collapsing removes rows *above* this button, so the surrounding content
    // shrinks and the browser clamps the drawer's scroll — the pane appears to
    // jump to the bottom. Anchor on the button so it stays where it was.
    const rootRef = useRef<HTMLDivElement>(null);
    const { capture } = usePreserveScrollAnchor(rootRef, isExpanded);

    const handleCollapse = useCallback(() => {
      capture();
      onCollapse();
    }, [capture, onCollapse]);

    if (isExpanded) {
      return (
        <div ref={rootRef} className={cx(styles['container'])}>
          <ButtonV2
            color='primary'
            isTertiary
            size='md'
            rightIcon={<IconExpanded size={16} />}
            onClick={handleCollapse}
            testId={`${testIdPrefix}-collapse-button`}
            className={cx(styles['container'])}
            containerClassName={cx(styles['button'])}
            buttonClassName={cx(styles['buttonText'])}
          >
            {collapseLabel}
          </ButtonV2>
        </div>
      );
    }

    return (
      <div
        ref={rootRef}
        className={cx(styles['container'], { loading: isLoading })}
      >
        <ButtonV2
          color='primary'
          isTertiary
          size='md'
          rightIcon={isLoading ? undefined : <IconCollapsed size={16} />}
          isDisabled={isLoading}
          onClick={onShowRemaining}
          testId={`${testIdPrefix}-show-remaining-button`}
          containerClassName={cx(styles['button'])}
          buttonClassName={cx(styles['buttonText'])}
        >
          {isLoading ? <Loading color='default' size='large' /> : showAllLabel}
        </ButtonV2>
      </div>
    );
  }
);
ShowRemainingButton.displayName = 'ShowRemainingButton';
